import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "@/api/client";
import { AdminUserIdentitySchema } from "@/api/admin/users";

/**
 * Admin generation journal (reading-feedback spec §6/§8).
 *
 * Schemas mirror the live FastAPI shapes in
 * `src/api/schemas/admin_generations.py`:
 *   - `GenerationStatsDTO` — `GET /api/admin/generations/stats`
 *   - `GenerationListDTO`  — `GET /api/admin/generations`
 *   - `GenerationDetailDTO`— `GET /api/admin/generations/{id}`
 *
 * The list/detail split is the point: the list carries a truncated
 * preview and never a prompt, the detail carries everything and costs an
 * `audit_log` row to look at — so the detail hook only runs when an admin
 * explicitly opens one row.
 */

export const GenerationVoteSchema = z.object({
  user_id: z.number().int().nullable(),
  rating: z.enum(["up", "down"]),
  reason: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type GenerationVote = z.infer<typeof GenerationVoteSchema>;

/** The configuration snapshot — everything except what was written. */
export const GenerationBaseSchema = z.object({
  id: z.number().int(),
  created_at: z.string(),
  user_id: z.number().int().nullable(),
  operation: z.string(),
  model: z.string(),
  reasoning_effort: z.string().nullable(),
  temperature: z.number().nullable(),
  max_tokens: z.number().int(),
  prompt_template: z.string(),
  locale: z.string(),
  platform: z.string(),
  tokens_in: z.number().int(),
  tokens_out: z.number().int(),
  reasoning_tokens: z.number().int().nullable(),
  cost_usd: z.number(),
  latency_ms: z.number().int(),
  reading_id: z.number().int().nullable(),
  votes: z.array(GenerationVoteSchema),
});

export const GenerationRowSchema = GenerationBaseSchema.extend({
  // The same identity block every other admin list carries, so the row can
  // link to `/admin/users/{id}` with a readable label instead of `#123`.
  // Null when there is no account left to name (anonymised row).
  user: AdminUserIdentitySchema.nullable(),
  preview: z.string(),
});
export type GenerationRow = z.infer<typeof GenerationRowSchema>;

export const GenerationListSchema = z.object({
  items: z.array(GenerationRowSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type GenerationList = z.infer<typeof GenerationListSchema>;

export const GenerationDetailSchema = GenerationBaseSchema.extend({
  prompt_hash: z.string(),
  context: z.record(z.unknown()).nullable(),
  output_text: z.string(),
  system_prompt: z.string().nullable(),
  user_prompt: z.string().nullable(),
});
export type GenerationDetail = z.infer<typeof GenerationDetailSchema>;

export const GenerationStatsRowSchema = z.object({
  operation: z.string(),
  model: z.string(),
  reasoning_effort: z.string().nullable(),
  generations: z.number().int(),
  votes: z.number().int(),
  up_votes: z.number().int(),
  down_votes: z.number().int(),
  // null, not 0, when nothing was voted on — an unjudged configuration is
  // not a badly-judged one.
  up_share_pct: z.number().nullable(),
  response_rate_pct: z.number().nullable(),
});
export type GenerationStatsRow = z.infer<typeof GenerationStatsRowSchema>;

/**
 * Where the vote counts came from. `rollup` is the default path —
 * `generation_feedback_daily` for closed days, raw votes for today. `raw`
 * means the window was counted off `llm_generations` instead, the only way
 * to honour a locale/platform slice: the rollup's key carries neither, so a
 * sliced number inherits the journal's retention bound. Defaulted for a
 * rolling deploy against a backend that predates the field.
 */
export const StatsSourceSchema = z.enum(["rollup", "raw"]).default("rollup");
export type StatsSource = z.infer<typeof StatsSourceSchema>;

export const GenerationStatsSchema = z.object({
  since: z.string(),
  until: z.string(),
  locale: z.string().nullable().default(null),
  platform: z.string().nullable().default(null),
  source: StatsSourceSchema,
  rows: z.array(GenerationStatsRowSchema),
});
export type GenerationStats = z.infer<typeof GenerationStatsSchema>;

/** `rating` filter on the list: liked, disliked, or nobody judged it. */
export const RATING_FILTERS = ["up", "down", "none"] as const;
export type RatingFilter = (typeof RATING_FILTERS)[number];

export interface GenerationFilters {
  operation?: string;
  model?: string;
  reasoningEffort?: string;
  locale?: string;
  userId?: number;
  rating?: RatingFilter;
  /** Inclusive calendar day, `YYYY-MM-DD`. */
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

function buildListQS(f: GenerationFilters): string {
  const params = new URLSearchParams();
  if (f.operation) params.set("operation", f.operation);
  if (f.model) params.set("model", f.model);
  if (f.reasoningEffort) params.set("reasoning_effort", f.reasoningEffort);
  if (f.locale) params.set("locale", f.locale);
  if (f.userId !== undefined) params.set("user_id", String(f.userId));
  if (f.rating) params.set("rating", f.rating);
  if (f.dateFrom) params.set("date_from", f.dateFrom);
  if (f.dateTo) params.set("date_to", f.dateTo);
  params.set("limit", String(f.limit ?? 50));
  params.set("offset", String(f.offset ?? 0));
  return params.toString();
}

export interface GenerationStatsWindow {
  /** Inclusive calendar days, `YYYY-MM-DD`. Omitted -> backend's last 30. */
  since?: string;
  until?: string;
  /**
   * Narrow the table to the same slice the list below shows. Either one
   * makes the response `source: "raw"` — see `StatsSourceSchema`.
   */
  locale?: string;
  platform?: string;
}

export function useGenerationStats(win: GenerationStatsWindow) {
  return useQuery({
    queryKey: ["admin", "generations", "stats", win],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (win.since) params.set("since", win.since);
      if (win.until) params.set("until", win.until);
      if (win.locale) params.set("locale", win.locale);
      if (win.platform) params.set("platform", win.platform);
      const qs = params.toString();
      return request({
        path: `/api/admin/generations/stats${qs ? `?${qs}` : ""}`,
        schema: GenerationStatsSchema,
        signal,
      });
    },
  });
}

export function useGenerations(filters: GenerationFilters) {
  return useQuery({
    queryKey: ["admin", "generations", "list", filters],
    queryFn: ({ signal }) =>
      request({
        path: `/api/admin/generations?${buildListQS(filters)}`,
        schema: GenerationListSchema,
        signal,
      }),
  });
}

/**
 * One generation in full. Fetching writes an `audit_log` row on the
 * server, so this is disabled until an admin opens a row, and the result
 * is never prefetched.
 */
export function useGeneration(id: number | null) {
  return useQuery({
    queryKey: ["admin", "generations", "detail", id],
    enabled: id !== null,
    queryFn: ({ signal }) =>
      request({
        path: `/api/admin/generations/${String(id)}`,
        schema: GenerationDetailSchema,
        signal,
      }),
  });
}
